import { useCallback, useSyncExternalStore } from 'react';

/**
 * App clock: local wall time (Date.now) plus an offset to server time, for every comparison with a
 * server time (market deadline, ledger as_of, activity start).
 *
 * Server time comes from the HTTP `Date` header of API responses (the sampling hook in
 * utils/api.ts, which takes only a header that passes parseDateHeader). Each sample replaces the
 * offset (no smoothing); a sample more than MAX_OFFSET_S from the device clock, or with a round trip
 * over MAX_SAMPLE_RTT_S, is skipped (see nextPlausibleOffset). Before the first sample the clock is
 * the device clock, unchanged.
 *
 * HEADER_FLOOR_BIAS_S and the code from MAX_SAMPLE_RTT_S to parseDateHeader are copies of
 * pages/btc-5m/clock.ts: keep the two files identical.
 * This module imports nothing but React, so any module (stores included) can use it.
 */

/**
 * The Date header is floored to whole seconds, so on average the true stamp is 0.5s later. Without
 * this the clock runs ~0.5s slow. Not applied to the `serverTime` fallback (the btc-5m rounds.json
 * field), whose staleness dwarfs the truncation.
 */
export const HEADER_FLOOR_BIAS_S = 0.5;

/**
 * A sample whose round trip took longer than this does not move the offset. The midpoint rule is
 * off by up to rtt/2, so a device that sleeps S seconds mid-request would put the clock S/2 off
 * (5 minutes for a 10-minute sleep) until the next fetch. Normal fetches take well under a second.
 */
export const MAX_SAMPLE_RTT_S = 3;

/**
 * A sample that puts server time more than this from the device clock does not move the offset.
 * parseDateHeader already drops a garbage Date header, so this bound only sets which device clocks
 * the server time corrects: a device clock up to 30 days off is still corrected.
 */
export const MAX_OFFSET_S = 30 * 86_400;

/** A Date header before 2026-01-01T00:00:00Z (epoch seconds) is not a server time. */
export const MIN_SERVER_DATE_S = 1_767_225_600;

// RFC 7231 IMF-fixdate ("Sat, 03 Oct 2026 10:00:00 GMT"): the only Date header form a sample takes.
const IMF_FIXDATE = /^[A-Z][a-z]{2}, \d{2} [A-Z][a-z]{2} \d{4} \d{2}:\d{2}:\d{2} GMT$/;

type ClockFeed = { serverTime: number; serverDate?: number };

/** Offset (server minus local wall time, seconds) from one fetch, using `serverDate` when known. */
export const clockOffset = (feed: ClockFeed, sentMs: number, receivedMs: number) => {
  const stamp = feed.serverDate !== undefined ? feed.serverDate + HEADER_FLOOR_BIAS_S : feed.serverTime;
  // The response was stamped between send and receive: assume the midpoint.
  const serverNow = stamp + (receivedMs - sentMs) / 2000;
  return serverNow - receivedMs / 1000;
};

/**
 * Offset after one fetch: the new sample, unless its round trip exceeded MAX_SAMPLE_RTT_S, in which
 * case the previous offset is kept. The first sample (no previous offset) is used even when slow:
 * an rtt/2 error is better than running on the raw device clock.
 */
export const nextOffset = (previous: number | undefined, feed: ClockFeed, sentMs: number, receivedMs: number) =>
  previous !== undefined && (receivedMs - sentMs) / 1000 > MAX_SAMPLE_RTT_S
    ? previous
    : clockOffset(feed, sentMs, receivedMs);

// True once a rejected sample is logged: one warning per page load is enough to diagnose a device.
let rejectionLogged = false;

/**
 * Logs the first sample of the session that the date floor or the offset bound rejects, so a device
 * clock more than MAX_OFFSET_S off can be diagnosed from the console.
 */
const logRejectedSample = (rejected: { header?: string | null; serverDate: number; offset?: number }) => {
  if (rejectionLogged) return;
  rejectionLogged = true;
  console.warn('[clock] rejected server Date sample', rejected);
};

/** Test-only: log the next rejected sample again. */
export const __resetRejectionLog = () => {
  rejectionLogged = false;
};

/**
 * nextOffset, except that a sample more than MAX_OFFSET_S from the device clock keeps the previous
 * offset (none before the first sample) and is logged. `header` is the raw Date header, for the log.
 */
export const nextPlausibleOffset = (
  previous: number | undefined,
  feed: ClockFeed,
  sentMs: number,
  receivedMs: number,
  header?: string | null
) => {
  const offset = clockOffset(feed, sentMs, receivedMs);
  if (Math.abs(offset) <= MAX_OFFSET_S) return nextOffset(previous, feed, sentMs, receivedMs);
  logRejectedSample({ header, serverDate: feed.serverDate ?? feed.serverTime, offset });
  return previous;
};

/**
 * A `Date` header in epoch seconds; undefined unless it is an IMF-fixdate not before MIN_SERVER_DATE_S.
 * Date.parse alone also takes "0" (the year 2000), other date forms and dates without a zone (read as
 * local time). A date before MIN_SERVER_DATE_S is logged.
 */
export const parseDateHeader = (header: string | null | undefined) => {
  const serverDate = header && IMF_FIXDATE.test(header) ? Date.parse(header) / 1000 : Number.NaN;
  if (serverDate < MIN_SERVER_DATE_S) logRejectedSample({ header, serverDate });
  // False for NaN: a date that Date.parse cannot read is dropped too.
  return serverDate >= MIN_SERVER_DATE_S ? serverDate : undefined;
};

// Seconds; undefined until the first sample.
let offset: number | undefined;
const listeners = new Set<() => void>();

/** Server time in epoch milliseconds; exactly the device clock until the first sample. */
export const serverNowMs = () => {
  const now = Date.now();
  return offset === undefined ? now : now + offset * 1000;
};

/** Server time in epoch seconds (with a fraction). */
export const serverNow = () => serverNowMs() / 1000;

export type ClockSample = {
  /** The response `Date` header, epoch ms. */
  serverDateMs: number;
  /** Local wall time when the request was sent and when its response arrived. */
  sentMs: number;
  receivedMs: number;
  /** The raw `Date` header, for the rejected-sample log. */
  header?: string | null;
};

/** Takes one response's Date header; subscribers hear of it when the offset changes. */
export const recordSample = ({ serverDateMs, sentMs, receivedMs, header }: ClockSample) => {
  if (![serverDateMs, sentMs, receivedMs].every(Number.isFinite)) return;
  const serverDate = serverDateMs / 1000;
  const next = nextPlausibleOffset(offset, { serverTime: serverDate, serverDate }, sentMs, receivedMs, header);
  if (next === offset) return;
  offset = next;
  listeners.forEach(listener => listener());
};

/** Calls back on every offset change; returns the unsubscribe function. */
export const subscribeClock = (listener: () => void) => {
  listeners.add(listener);
  return () => void listeners.delete(listener);
};

// setTimeout fires at once for a delay over 2^31 - 1 ms; a longer wait is re-armed.
const MAX_TIMER_MS = 2 ** 31 - 1;

const isExpired = (deadlineSec: number) => Number.isFinite(deadlineSec) && serverNow() >= deadlineSec;

/**
 * True once server time reaches `deadlineSec` (epoch seconds). The caller re-renders only when the
 * value flips: one timer to the deadline, re-armed when the offset changes; no interval. A missing
 * deadline (NaN, e.g. while the market loads) is never expired.
 */
export const useExpired = (deadlineSec: number) => {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!Number.isFinite(deadlineSec)) return () => undefined;
      let timer: ReturnType<typeof setTimeout> | undefined;
      const arm = () => {
        clearTimeout(timer);
        const wait = Math.max(deadlineSec * 1000 - serverNowMs(), 0);
        timer = isExpired(deadlineSec) ? undefined : setTimeout(check, Math.min(wait, MAX_TIMER_MS));
      };
      // A timer can fire before the wall clock reaches the deadline (long waits, clock changes): re-arm.
      const check = () => {
        onChange();
        arm();
      };
      arm();
      const unsubscribe = subscribeClock(check);
      return () => {
        clearTimeout(timer);
        unsubscribe();
      };
    },
    [deadlineSec]
  );
  const snapshot = () => isExpired(deadlineSec);
  return useSyncExternalStore(subscribe, snapshot, snapshot);
};

/** Test-only: forget the offset and the rejected-sample log (module state outlives a test). */
export const __resetClock = () => {
  offset = undefined;
  __resetRejectionLog();
  listeners.forEach(listener => listener());
};
